import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../config';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const refreshClient = axios.create({ withCredentials: true });
    let refreshing = null;
    const getRefreshPromise = () => {
      if (!refreshing) {
        refreshing = refreshClient
          .post(`${API}/auth/refresh`)
          .finally(() => {
            refreshing = null;
          });
      }
      return refreshing;
    };

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error?.response?.status;
        const originalConfig = error?.config || {};
        const reqUrl = String(originalConfig?.url || '');
        // Never try cookie-refresh for unauthenticated auth calls — that hides
        // the real error (e.g. Invalid credentials → "No refresh token").
        const isAuthBootstrap =
          reqUrl.includes('/auth/login') ||
          reqUrl.includes('/auth/forgot-password') ||
          reqUrl.includes('/auth/reset-password') ||
          reqUrl.includes('/auth/refresh');
        const alreadyRetried = !!originalConfig._retryAfterRefresh;
        if (status !== 401 || isAuthBootstrap || alreadyRetried) {
          return Promise.reject(error);
        }
        try {
          await getRefreshPromise();
          originalConfig._retryAfterRefresh = true;
          originalConfig.withCredentials = true;
          return axios(originalConfig);
        } catch (refreshErr) {
          setUser(null);
          return Promise.reject(refreshErr);
        }
      }
    );

    checkAuth();
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      // Access JWT expires after 15m; try refresh cookie (7d) then retry /me
      if (error.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const retry = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(retry.data);
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
